import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../../api/client';
import '../../backlog/backlog.css';
import './suboutRegistration.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { GridSearch } from '../../../components/GridSearch';
import { useNotify } from '../../../components/notifications';
import { useConfirm } from '../../../components/confirm';
import { useNavActions } from '../../../hooks/useNavActions';
import { DateRangeSelect, TRACKING_RANGES } from '../../modules/DateRangeSelect';
import { DEFAULT_DATE_RANGE } from '../../../components/listDefaults';
import { fmtLocalDate } from '../../../utils/datetime';
import type { SuboutOrderRow } from '../SuboutListPage';
import SuboutRegistrationDetail from './SuboutRegistrationDetail';
import {
  apiMessage, deleteTestOrders, fetchSuboutLookups, fetchTestOrder, fetchTestOrderSamples, submitTestOrders,
} from './api';
import type { SuboutLookups, SuboutSampleDetailRow, SuboutTestOrderDetail } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Sample SubContract Tracking → Subout Test Order (nav `subouttest`).
// The editable order list (legacy SampleParameter_ListView_Copy_TestOrder):
// Reports · Edit · Delete · Refresh · Date Filter · Submit · Export. Edit opens
// the order's detail view in edit mode. See deploy/db/AI_SuboutTestOrder.sql.
//
// DEFERRED (shown but not wired): Reports (Subout COC). Submit's per-order
// "all JobIds must be selected" rule and Tracking# guard are enforced.
// ─────────────────────────────────────────────────────────────────────────────

const FORM_NAME = 'Subout Test Order';
type DateRangeKey = '1M' | '3M' | '6M' | '1Y' | 'All';

function sinceFromKey(k: DateRangeKey): string {
  if (k === 'All') return '2020-01-01';
  const now = new Date();
  const map: Record<Exclude<DateRangeKey, 'All'>, () => Date> = {
    '1M': () => new Date(now.getFullYear(), now.getMonth() - 1, now.getDate()),
    '3M': () => new Date(now.getFullYear(), now.getMonth() - 3, now.getDate()),
    '6M': () => new Date(now.getFullYear(), now.getMonth() - 6, now.getDate()),
    '1Y': () => new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()),
  };
  const d = map[k as Exclude<DateRangeKey, 'All'>]();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const fd = (v?: string | null) => fmtLocalDate(v) || '—';

const COLUMNS: AppColumn<SuboutOrderRow>[] = [
  { field: 'JobID',          header: 'JobID',          width: 110, text: (r) => r.jobID ?? r.jobIDs ?? '', render: (r) => (r.jobID ?? r.jobIDs) || '—' },
  { field: 'SuboutOrderID',  header: 'SuboutOrderID',  width: 130, text: (r) => r.orderId, render: (r) => r.orderId || '—' },
  { field: 'DateShipped',    header: 'DateShipped',    width: 110, type: 'date', text: (r) => fd(r.dateShipped), render: (r) => fd(r.dateShipped) },
  { field: 'TrackingNum',    header: 'Tracking#',      width: 130, text: (r) => r.trackingNo ?? '', render: (r) => r.trackingNo || '—' },
  { field: 'NumSamples',     header: '#Samples',       width: 80, type: 'number', text: (r) => String(r.sampleCount ?? 0), value: (r) => r.sampleCount ?? 0 },
  { field: 'ContractLab',    header: 'ContractLab',    width: 190, text: (r) => r.contractLabName ?? '', render: (r) => r.contractLabName || '—' },
  { field: 'LabAddress',     header: 'LabAddress',     width: 190, text: (r) => r.labAddress ?? r.contractLabLocation ?? '', render: (r) => (r.labAddress ?? r.contractLabLocation) || '—' },
  { field: 'TurnAroundTime', header: 'TurnAroundTime', width: 120, text: (r) => r.turnAroundTime ?? '', render: (r) => r.turnAroundTime || '—' },
  { field: 'DueDate',        header: 'DueDate',        width: 110, type: 'date', text: (r) => fd(r.dueDate), render: (r) => fd(r.dueDate) },
  { field: 'Status',         header: 'Status',         width: 200, type: 'status', text: (r) => r.statusText },
  { field: 'StatusID',       header: 'StatusID',       width: 80, text: (r) => r.statusId ?? '', render: (r) => r.statusId || '—' },
];

const orderOidOf = (r: SuboutOrderRow): string => r.id.split(':')[0] ?? r.id;

export default function SuboutTestOrderPage() {
  const notify = useNotify();
  const confirm = useConfirm();
  const perm = useNavActions('subouttest');

  const [rows, setRows]       = useState<SuboutOrderRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadMs, setLoadMs]   = useState<number | null>(null);
  const [dateRange, setDateRange] = useState<DateRangeKey>(DEFAULT_DATE_RANGE.SuboutTracking as DateRangeKey);
  const [search, setSearch]   = useState('');
  const [statusChip, setStatusChip] = useState<string>('all');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const res = await api.get('/api/v1/subout/test-orders', { params: { sinceDate: sinceFromKey(dateRange) } });
      setRows((res.data as { data: SuboutOrderRow[] }).data ?? []);
      setSelected(new Set());
    } catch (e) {
      notify.error(apiMessage(e, 'Failed to load the subout test orders.'));
      setRows([]);
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [dateRange, notify]);

  useEffect(() => { void load(); }, [load]);

  const [lookups, setLookups] = useState<SuboutLookups | null>(null);
  useEffect(() => {
    fetchSuboutLookups().then(setLookups).catch(() => {/* lookups are best-effort */});
  }, []);

  const searchFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [r.orderId, r.contractLabName, r.client, r.jobID, r.testForSubout, r.trackingNo]
      .some((v) => (v ?? '').toLowerCase().includes(q)));
  }, [rows, search]);

  const visible = useMemo(() => (statusChip === 'all'
    ? searchFiltered
    : searchFiltered.filter((r) => r.statusText === statusChip)), [searchFiltered, statusChip]);

  const chips = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of searchFiltered) m.set(r.statusText, (m.get(r.statusText) ?? 0) + 1);
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [searchFiltered]);

  // Resolve the selected orders, enforcing the legacy "all JobIds of an order
  // must be selected" rule against the currently displayed rows.
  const resolveSelection = useCallback(() => {
    const total = new Map<string, number>(), sel = new Map<string, number>(), name = new Map<string, string>();
    for (const r of visible) {
      const oid = orderOidOf(r);
      total.set(oid, (total.get(oid) ?? 0) + 1);
      name.set(oid, r.orderId ?? '');
      if (selected.has(r.id)) sel.set(oid, (sel.get(oid) ?? 0) + 1);
    }
    const oids = [...sel.keys()];
    for (const oid of oids) {
      const t = total.get(oid) ?? 0, s = sel.get(oid) ?? 0;
      if (s < t) return { oids, partial: { orderId: name.get(oid) ?? '', total: t, selected: s, remaining: t - s } };
    }
    return { oids, partial: null as null | { orderId: string; total: number; selected: number; remaining: number } };
  }, [visible, selected]);

  // ── Edit (single order → detail edit view) ─────────────────────────────────
  const [editOrder, setEditOrder]     = useState<SuboutTestOrderDetail | null>(null);
  const [editSamples, setEditSamples] = useState<SuboutSampleDetailRow[]>([]);

  // Open one order's detail in edit mode (row pencil, or the toolbar Edit).
  const openEditOrder = useCallback(async (oid: string) => {
    if (!perm.write) { notify.warning('You do not have permission to edit subout orders.'); return; }
    setBusy(true);
    try {
      const [order, samples] = await Promise.all([fetchTestOrder(oid), fetchTestOrderSamples(oid)]);
      setEditOrder(order); setEditSamples(samples);
    } catch (e) {
      notify.error(apiMessage(e, 'Failed to open the order.'));
    } finally {
      setBusy(false);
    }
  }, [perm, notify]);

  function openEdit() {
    const { oids } = resolveSelection();
    const oid = oids[0];
    if (oids.length !== 1 || !oid) { notify.warning('Select one order to edit.'); return; }
    void openEditOrder(oid);
  }

  // The grid columns, with the per-row Edit pencil first (ALPACA parity).
  const cols = useMemo<AppColumn<SuboutOrderRow>[]>(() => [
    {
      field: 'edit', header: '', width: 38, align: 'center', noExport: true, sortable: false, filterable: false,
      render: (r) => (
        <button type="button" className="srd-rowedit" title="Edit"
                onClick={(ev) => { ev.stopPropagation(); void openEditOrder(orderOidOf(r)); }}>
          <i className="ti ti-edit" aria-hidden="true" />
        </button>
      ),
    },
    ...COLUMNS,
  ], [openEditOrder]);

  // ── Submit / Delete (bulk) ─────────────────────────────────────────────────
  async function doAction(kind: 'submit' | 'delete') {
    if (kind === 'submit' && !perm.write)  { notify.warning('You do not have permission to submit subout orders.'); return; }
    if (kind === 'delete' && !perm.delete) { notify.warning('You do not have permission to delete subout orders.'); return; }
    const { oids, partial } = resolveSelection();
    if (oids.length === 0) { notify.warning('Please select an item to proceed further.'); return; }
    if (partial) {
      notify.warning(`Selected SuboutOrderId ${partial.orderId} has ${partial.total} JobId(s). You selected only ${partial.selected}. Please select the remaining ${partial.remaining} JobId(s) as well.`);
      return;
    }
    const ok = await confirm.confirm({
      title: kind === 'submit' ? 'Submit' : 'Delete',
      tone: kind === 'delete' ? 'danger' : undefined,
      message: `${kind === 'submit' ? 'Submit' : 'Delete'} ${oids.length} subout order${oids.length === 1 ? '' : 's'}?`,
    });
    if (!ok) return;
    setBusy(true);
    try {
      const res = kind === 'submit' ? await submitTestOrders(oids) : await deleteTestOrders(oids);
      const done = kind === 'submit' ? (res as { submitted: number }).submitted : (res as { deleted: number }).deleted;
      const skipped = res.rows.filter((r) => !r.ok);
      if (done === 0) notify.warning(skipped[0]?.outcome ?? 'Nothing changed.');
      else if (skipped.length) notify.warning(`${kind === 'submit' ? 'Submitted' : 'Deleted'} ${done}. ${skipped.length} skipped: ${skipped[0]?.outcome ?? ''}`);
      else notify.success(kind === 'submit' ? 'Submitted successfully.' : 'Deleted successfully.');
      await load();
    } catch (e) {
      notify.error(apiMessage(e, 'The request failed.'));
    } finally {
      setBusy(false);
    }
  }

  // Edit detail view REPLACES the list (standing rule 12).
  if (editOrder) {
    return (
      <SuboutRegistrationDetail
        edit={{ order: editOrder, samples: editSamples }}
        lookups={lookups}
        canCreate={perm.write}
        onClose={() => setEditOrder(null)}
        onSaved={() => { setEditOrder(null); void load(); }}
      />
    );
  }

  return (
    <div className="bl-page">
      <FormTitle pageId="subouttest" loadMs={loadMs} loading={loading} auditFormName={FORM_NAME} />

      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={
              <>
                <button type="button" className="ca-btn" disabled title="Reports (Subout COC) — not yet available">
                  <i className="ti ti-file-text" aria-hidden="true" /> Reports
                </button>
                <DateRangeSelect variant="dropdown" label="Date Filter" ranges={TRACKING_RANGES}
                  value={TRACKING_RANGES.find((r) => r.label === dateRange)?.days ?? null}
                  onChange={(d) => setDateRange((TRACKING_RANGES.find((r) => r.days === d)?.label ?? 'All') as DateRangeKey)} />
              </>
            }
            onEdit={openEdit}
            editDisabled={busy || loading}
            onDelete={() => void doAction('delete')}
            deleteDisabled={busy || loading}
            beforeExport={
              <button type="button" className="ca-btn" onClick={() => void doAction('submit')}
                      disabled={busy || loading || (perm ? !perm.write : false)}>
                <i className="ti ti-send" aria-hidden="true" /> Submit
              </button>
            }
            onRefresh={load}
            refreshing={loading}
            exportData={{ filename: 'subout-test-order', ...gridExportMatrix(visible, COLUMNS) }}
            perm={perm}
          />
        </div>

        <div className="list-search-row">
          <GridSearch value={search} onChange={setSearch} placeholder="Search order ID, lab, client, job, test…" />
          {selected.size > 0 && <span className="search-match-count">{selected.size} selected</span>}
        </div>

        <div className="bl-card bl-card-flex">
          {chips.length > 0 && (
            <div className="sa-filter-row">
              <span className="sa-filter-lbl">Status:</span>
              <button className={`sa-filter-btn${statusChip === 'all' ? ' active' : ''}`}
                      onClick={() => { setStatusChip('all'); setSelected(new Set()); }}>
                All ({searchFiltered.length})
              </button>
              {chips.map(([label, n]) => (
                <button key={label} className={`sa-filter-btn${statusChip === label ? ' active' : ''}`}
                        onClick={() => { setStatusChip(label); setSelected(new Set()); }}>
                  {label} ({n})
                </button>
              ))}
            </div>
          )}

          <AppDataGrid<SuboutOrderRow>
            rows={visible}
            columns={cols}
            getRowId={(r) => r.id}
            loading={loading}
            loadMs={loadMs}
            selectable
            selectedIds={selected}
            onSelectionChange={setSelected}
            emptyText="No subout test orders for this date range."
          />
        </div>
      </div>
    </div>
  );
}
